import React, { useState, useEffect } from 'react';
import { startOfDay, endOfDay } from 'date-fns';
import styles from './InpatientPortal.module.css';
import VitalCard from './components/VitalCard';
import BatteryCard from './components/BatteryCard';
import InpatientVitalChart from './components/charts/InpatientVitalChart';
import { LeftArrowIcon } from '../../components/icons/Icons';
import { convertTemperature, convertWeight, getVitalUnit } from '../../utils/unitUtils';
import { useSelector, useDispatch } from 'react-redux';
import useAxios from '../../services/AxiosConfig';
import { formatDateString, formatDateStringWithoutYear } from '../../utils/dateUtils';
import InpatientAuscChart from './components/charts/InpatientAuscChart';
import InpatientBloodPressureChart from './components/charts/InpatientBloodPressureChart';
import InpatientStepChart from './components/charts/InpatientStepChart';
import { openModal, closeModal } from '../../components/PerinHealthModelSlice';
import PerinHealthModal from '../../components/PerinHealthModel';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import CustomToggle from '../../components/CustomToggle';
import ChartDetailView from '../patient/modality-charts/ChartDetailView';

const InpatientDetail = ({ patient, onBack, onDischarge }) => {

    const { dateFormat, unitType } = useSelector((state) => state.userInfo);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);

    // Initialize startDate and endDate to today's start and end of day
    // Use yesterday instead of today
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const initialStartDate = startOfDay(yesterday).toISOString();
    const initialEndDate = endOfDay(yesterday).toISOString();

    const [vitalsConfig, setVitalsConfig] = useState([]);
    const [selectedVital, setSelectedVital] = useState('ECG');
    const [averageGrouped, setAverageGrouped] = useState(false);
    const [measurements, setMeasurements] = useState([]);
    const [startDate, setStartDate] = useState(initialStartDate);
    const [serialNumber, setSerialNumber] = useState('');
    const [endDate, setEndDate] = useState(initialEndDate);
    const [readingInterval, setReadingInterval] = useState('Day');
    const [auscData, setAuscData] = useState([]);
    const [dateSelection, setDateSelection] = useState('today'); // 'today' or 'yesterday'

    const patientName = patient?.fullName || patient;
    const floorRoom = `${patient?.location} | Floor ${patient?.floor} | ${patient?.room} | ${patient?.bed}`;

    // Update dates when date selection changes
    useEffect(() => {
        const selectedDate = dateSelection === 'today' ? new Date() : (() => {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return yesterday;
        })();

        setStartDate(startOfDay(selectedDate).toISOString());
        setEndDate(endOfDay(selectedDate).toISOString());
    }, [dateSelection]);

    const handlePointClick = (measurement, vitalConfig) => {
        if (!measurement || !vitalConfig) return;

        
        const timeStamp = measurement.timeStamp;
        const deviceName = measurement.deviceName || '';
        const modalityId = vitalConfig.modalityId;
        const modalityName = vitalConfig.title || vitalConfig.name || '';
        const modalityType = vitalConfig.modalityType || '';
        
        setModalContent({ 
            timeStamp, 
            deviceName, 
            modalityId, 
            vital: modalityName, 
            modalityType,
            patientId: patient?.id
        });
        setIsModalOpen(true);
    };

    useEffect(() => {
        const enrollment = patient?.enrollment ? patient.enrollment[0] : { 'devices': [] };
        const allVitals = [];
        const vitalsAdded = new Set();
        enrollment.devices.forEach(device => {
            setSerialNumber(device.serialNumber);
            let name = null;
            let title = null;
            switch (device.modalityId) {
                case 3:
                    name = 'oxygenSaturation';
                    title = 'Oxygen Saturation';
                    break;
                case 4:
                    name = 'ECG';
                    title = 'Heart Rate';
                    break;
                case 15:
                    name = 'ECGHRV';
                    title = 'Heart Rate Variability';
                    break;
                case 5:
                case 10:
                    name = 'bloodPressure';
                    title = 'Blood Pressure';
                    break;
                case 1:
                    name = 'temperature';
                    title = 'Temperature';
                    break;
                case 6:
                    name = 'respiratoryRate';
                    title = 'Respiratory Rate';
                    break;
                case 8:
                    name = 'weight';
                    title = 'Weight';
                    break;
                case 7:
                    name = 'AUSC';
                    title = 'Audio';
                    break;
                case 13:
                    name = 'steps';
                    title = 'Step Count';
                    break;
                case 11:
                    name = 'glucoseLevel';
                    title = 'Glucose Level';
                    break;
                default:
                    break;
            }
            if (name && !vitalsAdded.has(name)) {
                const vitalUnit = getVitalUnit(device.modalityId, unitType);
                allVitals.push({
                    name,
                    title,
                    modalityId: device.modalityId,
                    modalityType: device.modalityType,
                    upperLimit: device.upperLimit,
                    lowerLimit: device.lowerLimit,
                    targetValue: device.targetValue,
                    unit: vitalUnit
                });
                vitalsAdded.add(name);
            }

        });
        setSelectedVital(allVitals.find(vital => vital.name === 'ECG'));
        setVitalsConfig(allVitals);
    }, [patient, startDate, endDate]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`device/readings/${patient?.id}?startDate=${startDate}&endDate=${endDate}&interval=${readingInterval}`);
                const fetchedData = response.data.data;

                const filteredData = fetchedData.filter(reading => reading.modalityId !== 7)
                const auscData = fetchedData.filter(reading => reading.modalityId === 7)
                let groupedByAvg = false;
                if (filteredData.length >= 1) {
                    groupedByAvg = filteredData[0].averageGrouped;
                    setAverageGrouped(groupedByAvg);
                }

                // Group by modality instead of by date
                const groupedData = {};
                // Sort filteredData by timestamp first
                const sortedFilteredData = [...filteredData].sort((a, b) => {
                    return new Date(a.completedDate) - new Date(b.completedDate);
                });

                sortedFilteredData.forEach(reading => {
                    const modalityId = reading.modalityId;

                    // Initialize modality group if it doesn't exist
                    if (!groupedData[modalityId]) {
                        groupedData[modalityId] = {
                            modalityId: modalityId,
                            modalityName: reading.modalityName,
                            modalityType: reading.modalityType,
                            readings: []
                        };
                    }

                    const dateString = reading.completedDate;
                    const completedDate = new Date(dateString);
                    const formattedDate = formatDateStringWithoutYear(completedDate.toISOString(), dateFormat);
                    const hour = String(completedDate.getHours()).padStart(2, '0');
                    const minute = String(completedDate.getMinutes()).padStart(2, '0');

                    // Create groupKey for date/time grouping within modality
                    const groupKey = (groupedByAvg && readingInterval === 'Day') ?
                        formattedDate
                        : `${formattedDate}-${hour}:${minute}`;

                    // Find or create device group for this date/time within the modality
                    let deviceGroup = groupedData[modalityId].readings.find(r =>
                        r.groupKey === groupKey && r.deviceId === reading.deviceId
                    );

                    if (!deviceGroup) {
                        deviceGroup = {
                            groupKey,
                            displayKey: formattedDate,
                            timeStamp: dateString,
                            formattedDate,
                            sortTime: completedDate.getTime(),
                            deviceId: reading.deviceId,
                            deviceName: reading.model,
                            measurement: null,
                            measurementRange: null,
                            sqi: null,
                            adhoc: false
                        };
                        groupedData[modalityId].readings.push(deviceGroup);
                    }
                    // Process measurement value
                    let measurementVal = reading.measurement;
                    if (Array.isArray(reading.measurement)) {
                        if (Array.isArray(reading.measurement[0])) {
                            let averageVals = (reading.measurement.reduce((total, item) => total + item[0], 0)) / reading.measurement.length;
                            measurementVal = averageVals;
                        } else {
                            let averageVals = (reading.measurement.reduce((total, item) => total + item, 0)) / reading.measurement.length;
                            measurementVal = averageVals;
                        }
                    }
                    if (measurementVal?.pNN50 != null) {
                        // handles older hrv measurements
                        measurementVal = measurementVal.pNN50;
                    }
                    if (typeof reading.measurement === 'string') {
                        let floatVal = parseFloat(reading.measurement);
                        if (!isNaN(floatVal)) {
                            measurementVal = parseFloat(floatVal.toFixed(1));
                        }
                    } else if (typeof reading.measurement === 'number') {
                        measurementVal = parseFloat(reading.measurement.toFixed(1));
                    }

                    // Convert measurement based on modality and unit preference
                    if (modalityId === 1) {
                        // Temperature
                        measurementVal = convertTemperature(measurementVal, unitType);
                    } else if (modalityId === 8) {
                        // Weight
                        measurementVal = convertWeight(measurementVal, unitType);
                    }

                    // Handle SQI
                    if (reading.SQI_reading_accuracy === "unacceptable") {
                        measurementVal = null;
                    }

                    // Set measurement value
                    deviceGroup.measurement = measurementVal;
                    deviceGroup.sqi = reading.SQI_reading_accuracy || null;
                    deviceGroup.adhoc = reading.scheduledReading === false;

                    // Handle range values for averaged data
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        let maxVal = reading.groupMaxVal;
                        let minVal = reading.groupMinVal;

                        // Convert range values for temperature and weight
                        if (modalityId === 1) {
                            maxVal = convertTemperature(maxVal, unitType);
                            minVal = convertTemperature(minVal, unitType);
                        } else if (modalityId === 8) {
                            maxVal = convertWeight(maxVal, unitType);
                            minVal = convertWeight(minVal, unitType);
                        }

                        deviceGroup.measurementRange = {
                            max: parseFloat(maxVal.toFixed(1)),
                            min: parseFloat(minVal.toFixed(1))
                        };
                    }



                });

                // Process auscultation data (modalityId 7) - also group by modality
                if (auscData.length > 0 && !groupedData[7]) {
                    groupedData[7] = {
                        modalityId: 7,
                        modalityName: auscData[0].modalityName || 'Auscultation',
                        modalityType: auscData[0].modalityType,
                        readings: []
                    };
                }

                const sortedAuscData = [...auscData].sort((a, b) => {
                    return new Date(a.completedDate) - new Date(b.completedDate);
                });

                sortedAuscData.forEach(reading => {
                    const dateString = reading.completedDate;
                    const completedDate = new Date(dateString);
                    const formattedDate = formatDateStringWithoutYear(completedDate.toISOString(), dateFormat);
                    const hour = String(completedDate.getHours()).padStart(2, '0');
                    const minute = String(completedDate.getMinutes()).padStart(2, '0');

                    const groupKey = `${formattedDate}-${hour}:${minute}`;

                    let deviceGroup = groupedData[7].readings.find(r =>
                        r.groupKey === groupKey && r.deviceId === reading.deviceId
                    );

                    if (!deviceGroup) {
                        deviceGroup = {
                            groupKey,
                            displayKey: formattedDate,
                            timeStamp: dateString,
                            formattedDate,
                            sortTime: completedDate.getTime(),
                            deviceId: reading.deviceId,
                            deviceName: reading.model,
                            measurement: null,
                            measurementRange: null,
                            sqi: null,
                            adhoc: false
                        };
                        groupedData[7].readings.push(deviceGroup);
                    }

                    let measurementVal = reading.measurement;
                    if (typeof reading.measurement === 'string') {
                        measurementVal = 0;
                    }
                    deviceGroup.measurement = measurementVal;
                });

                // Transform grouped data by modality into the format needed
                const transformedData = [];
                const transformedAuscData = [];

                // Process each modality group
                Object.values(groupedData).forEach(modalityGroup => {
                    // Sort readings by time within each modality
                    const sortedReadings = [...modalityGroup.readings].sort((a, b) => {
                        return a.sortTime - b.sortTime;
                    });

                    sortedReadings.forEach(reading => {
                        // Format the full date and time for tooltip
                        const tooltipDate = new Date(reading.timeStamp);
                        const formattedTooltipDate = formatDateString(tooltipDate.toISOString(), dateFormat);
                        const formattedTime = tooltipDate.toLocaleString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true
                        });

                        const readingData = {
                            modalityId: modalityGroup.modalityId,
                            modalityName: modalityGroup.modalityName,
                            modalityType: modalityGroup.modalityType,
                            date: reading.groupKey,
                            displayDate: reading.displayKey,
                            formattedDate: reading.formattedDate,
                            tooltipDate: `${formattedTooltipDate} ${formattedTime}`,
                            timeStamp: reading.timeStamp,
                            sortTime: reading.sortTime,
                            deviceId: reading.deviceId,
                            deviceName: reading.deviceName,
                            measurement: reading.measurement,
                            measurementRange: reading.measurementRange,
                            sqi: reading.sqi,
                            adhoc: reading.adhoc
                        };

                        if (modalityGroup.modalityId === 7) {
                            transformedAuscData.push(readingData);
                        } else {
                            transformedData.push(readingData);
                        }
                    });
                });

                setAuscData(transformedAuscData);
                setMeasurements(transformedData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        if (patient?.id) {
            fetchData();
        }

    }, [startDate, endDate, readingInterval, dateFormat, unitType]);


    const handleDialogButtonClick = (onClickKey) => {
        if (onClickKey === 'YES') {
            dischargeHandler();
        } else {
            dispatch(closeModal());
        }
    };

    const dischargeHandler = async () => {
        dispatch(closeModal());
        try {
            const response = await axios.delete(`enrollment/inpatient/${patient.enrollment[0].enrollmentId}/${patient.id}`);
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Patient has been discharged",
                    type: 'success'
                })
            );
            onDischarge();
        } catch (error) {
            console.error('Error discharging patient:', error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "There was an error discharging the patient",
                    type: 'danger'
                })
            );
        }
    };


    return (
        <div className={styles.DetailCard}>
            <div style={{ width: '100%' }}>
                {onBack && (
                    <button className={styles.BackButton} onClick={onBack}>
                        <LeftArrowIcon /> Back to All Patients
                    </button>
                )}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', justifyContent: 'space-between', width: '100%' }}>
                    {/* Patient Info Section */}
                    <div className={styles.PatientInfoSection}>
                        <p className={styles.FloorRoomText}>
                            {floorRoom}
                        </p>
                        <p className={styles.PatientNameText}>
                            {patientName}
                        </p>
                        <div className={styles.DeviceInfoSection}>
                            <div className={styles.DeviceIdSection}>
                                <p className={styles.DeviceIdText}>
                                    Device: {serialNumber}
                                </p>
                            </div>
                            <BatteryCard
                                value={patient?.batteryLevel}
                            />

                        </div>
                    </div>
                    {/* Date Toggle Section */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CustomToggle
                            labels={[
                                { name: 'Today', value: 'today' },
                                { name: 'Yesterday', value: 'yesterday' }
                            ]}
                            selectedToggle={dateSelection}
                            handleToggle={(value) => setDateSelection(value)}
                        />
                    </div>
                </div>

            </div>

            {selectedVital.title === 'Audio' ? (
                <InpatientAuscChart
                    vitalConfig={selectedVital}
                    measurements={auscData}
                    onPointClick={handlePointClick}
                />
            ) : selectedVital.title === 'Blood Pressure' ? (
                <InpatientBloodPressureChart
                    vitalConfig={selectedVital}
                    measurements={measurements}
                />
            ) : selectedVital.title === 'Step Count' ? (
                    <InpatientStepChart
                        vitalConfig={selectedVital}
                        measurements={measurements}
                    />
                ) : (
                <InpatientVitalChart
                    vitalConfig={selectedVital}
                    measurements={measurements}
                    onPointClick={handlePointClick}
                />)}


            <div className={styles.VitalsGridSection}>
                <div className={styles.VitalsGrid}>
                    {vitalsConfig.map((vital) => (
                        <VitalCard
                            key={vital.modalityId}
                            vital={vital}
                            data={measurements}
                            isSelected={selectedVital.name === vital.name}
                            onClick={() => setSelectedVital(vital)}
                        />
                    ))}
                </div>
                <div className={styles.DischargeSection}>
                    <button
                        className={styles.DischargeButton}
                        onClick={() => {
                            dispatch(
                                openModal({
                                    activeModalId: "InpatientDetailDischarge",
                                    header: 'Discharge Patient?',
                                    message: `Are you sure you want to discharge this patient? <br /><span style="color: #dc3545; font-weight: bold;">This action cannot be undone.</span>`,
                                    buttons: [
                                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                                        { label: "Confirm", variant: 'primary', onClickKey: 'YES' },
                                    ],
                                })
                            );
                        }}
                    >
                        <div className={styles.DischargeButtonText}>
                            <p>Discharge Patient</p>
                        </div>
                    </button>
                    <div className={styles.DischargeWarning}>
                        <div className={styles.DischargeWarningText}>
                            <p>End patient monitoring when care is complete.</p>
                            <p>This action cannot be undone.</p>
                        </div>
                    </div>
                </div>
            </div>

            <PerinHealthModal
                modalId={"InpatientDetailDischarge"}
                handleButtonClick={handleDialogButtonClick}
            />
             <ChartDetailView
                isModalVisible={isModalOpen}
                customModal={styles.customModal}
                handleCloseModal={() => setIsModalOpen(false)}
                modalData={modalContent}
            />
        </div>
    );
};

export default InpatientDetail;